import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
/*
 * tableKit — shared column sort + filter headers for admin tables.
 *
 * Extracted from the Issues table's inline implementation (AQUASTAR-117).
 * Provides: click-to-sort column headers with ▲/▼/⇅ glyphs + funnel button
 * that opens a fixed-position popover with either an enum checkbox list
 * (caller-supplied options) or a free-text multi-term filter with badge chips.
 *
 * Sort is CONTROLLED (parent owns sortCol/sortDir state) so pages that share
 * sort across multiple views (e.g. Pumps cards + table) keep working.
 */
import { useState } from 'react';
/**
 * Column header component: click-to-sort label + optional filter funnel.
 * Renders a sticky header cell with sort arrows and a popover for filtering.
 */
export function ColHead({ colKey, label, sortable = true, sortCol, sortDir, onSort, filterable = false, mode = 'text', options = [], selected, onSelected, placeholder, }) {
    const [openFilter, setOpenFilter] = useState(false);
    const [filterAnchor, setFilterAnchor] = useState(null);
    const [draft, setDraft] = useState('');
    const isFiltered = selected.length > 0;
    const isSorted = sortCol === colKey;
    const arrow = isSorted ? (sortDir === 'asc' ? '▲' : '▼') : '⇅';
    const handleOpenFilter = (e) => {
        if (openFilter) {
            setOpenFilter(false);
            return;
        }
        const r = e.currentTarget.getBoundingClientRect();
        setFilterAnchor({ top: r.bottom + 2, left: r.left });
        setDraft('');
        setOpenFilter(true);
    };
    const toggleVal = (val) => {
        const next = selected.includes(val)
            ? selected.filter((x) => x !== val)
            : [...selected, val];
        onSelected(colKey, next);
    };
    const addTerm = (raw) => {
        const t = raw.trim();
        if (t && !selected.includes(t)) {
            onSelected(colKey, [...selected, t]);
        }
        setDraft('');
    };
    const removeVal = (val) => {
        onSelected(colKey, selected.filter((x) => x !== val));
    };
    const clearFilter = () => {
        onSelected(colKey, []);
    };
    return (_jsxs("th", { style: { whiteSpace: 'nowrap', position: 'sticky', top: 0, zIndex: 3, background: '#f1f3f5' }, children: [_jsxs("span", { className: "d-inline-flex align-items-center gap-1", children: [sortable ? (_jsxs("span", { role: "button", onClick: () => onSort(colKey), style: { cursor: 'pointer' }, children: [label, " ", _jsx("span", { className: isSorted ? '' : 'text-muted', style: { fontSize: '0.72em' }, children: arrow })] })) : (_jsx("span", { children: label })), filterable && (_jsxs("button", { type: "button", title: isFiltered ? `Filtered (${selected.length})` : 'Filter', className: `btn btn-sm p-0 border-0 bg-transparent position-relative ${isFiltered ? 'text-primary' : 'text-muted'}`, onClick: handleOpenFilter, children: [_jsx("svg", { width: "11", height: "11", viewBox: "0 0 16 16", fill: "currentColor", style: { verticalAlign: 'middle' }, children: _jsx("path", { d: "M1.5 2h13l-5 6v5.5l-3 1.5V8z" }) }), isFiltered && (_jsx("span", { className: "badge rounded-pill bg-primary", style: { fontSize: '0.5em', position: 'absolute', top: -5, right: -7 }, children: selected.length }))] }))] }), openFilter && filterAnchor && (_jsxs(_Fragment, { children: [_jsx("div", { onClick: () => setOpenFilter(false), style: { position: 'fixed', inset: 0, zIndex: 1040 } }), _jsxs("div", { className: "card shadow p-2", onClick: (e) => e.stopPropagation(), style: { position: 'fixed', top: filterAnchor.top, left: filterAnchor.left, zIndex: 1050, minWidth: 210, maxWidth: 300 }, children: [_jsxs("div", { className: "small text-muted mb-1 fw-semibold", children: ["Filter \u00B7 ", label, selected.length ? ` (${selected.length})` : ''] }), mode === 'enum' ? (_jsx("div", { style: { maxHeight: 220, overflowY: 'auto' }, children: options.length === 0 ? (_jsx("div", { className: "text-muted small", children: "no values" })) : (options.map((o) => (_jsxs("div", { className: "form-check", children: [_jsx("input", { className: "form-check-input", type: "checkbox", id: `flt-${colKey}-${o}`, checked: selected.includes(o), onChange: () => toggleVal(o) }), _jsx("label", { className: "form-check-label small", htmlFor: `flt-${colKey}-${o}`, children: o })] }, o)))) })) : (_jsxs(_Fragment, { children: [selected.length ? (_jsx("div", { className: "mb-1", children: selected.map((t) => (_jsxs("span", { className: "badge bg-secondary me-1 mb-1", style: { fontWeight: 500 }, children: [t, _jsx("span", { role: "button", "aria-label": `remove ${t}`, className: "ms-1", style: { cursor: 'pointer' }, onClick: () => removeVal(t), children: "\u00D7" })] }, t))) })) : null, _jsxs("div", { className: "d-flex gap-1", children: [_jsx("input", { className: "form-control form-control-sm", autoFocus: true, placeholder: placeholder || 'contains…', value: draft, onChange: (e) => setDraft(e.target.value), onKeyDown: (e) => {
                                                    if (e.key === 'Enter') {
                                                        e.preventDefault();
                                                        addTerm(draft);
                                                    }
                                                } }), _jsx("button", { type: "button", className: "btn btn-outline-secondary btn-sm", onClick: () => addTerm(draft), disabled: !draft.trim(), children: "Add" })] }), _jsx("div", { className: "form-text", style: { fontSize: '0.7rem' }, children: "matches rows containing any of these terms" })] })), _jsxs("div", { className: "d-flex justify-content-between mt-2", children: [_jsx("button", { type: "button", className: "btn btn-link btn-sm p-0 text-decoration-none", onClick: clearFilter, children: "Clear" }), _jsx("button", { type: "button", className: "btn btn-primary btn-sm py-0", onClick: () => setOpenFilter(false), children: "Done" })] })] })] }))] }));
}
/**
 * Apply column filters to a set of rows. Each column's filter is a list of
 * values; enum columns match exact values, text columns match substrings.
 * Returns the filtered subset.
 */
export function applyColumnFilters(rows, filters, cellVals, enumCols) {
    return rows.filter((r) => {
        const v = cellVals(r);
        for (const col of Object.keys(filters)) {
            const sel = filters[col];
            if (!sel.length)
                continue;
            const cell = v[col];
            if (enumCols.has(col)) {
                if (!sel.includes(cell))
                    return false;
            }
            else {
                if (!sel.some((term) => cell.toLowerCase().includes(term.toLowerCase())))
                    return false;
            }
        }
        return true;
    });
}
/**
 * Comparator factory for sorting by a derived value + direction.
 * Returns a comparator function suitable for Array.sort().
 */
export function compareBy(getVal, dir) {
    const mult = dir === 'asc' ? 1 : -1;
    return (a, b) => {
        const av = getVal(a);
        const bv = getVal(b);
        if (typeof av === 'string' && typeof bv === 'string') {
            return av.localeCompare(bv) * mult;
        }
        return (av - bv) * mult;
    };
}
//# sourceMappingURL=tableKit.js.map